import type { ClassValue } from 'clsx'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import { isEnvelopesListPath } from '@/lib/envelopes-path'

export function cn(...inputs: Array<ClassValue>) {
  return twMerge(clsx(inputs))
}

/**
 * Determines if the current route requires a redirection
 * before performing operations that may invalidate the current context.
 *
 * This function checks if the user is in a context-specific subdirectory
 * and returns the appropriate redirect URL to prevent them from staying
 * in a page that may no longer be valid after operations like account
 * switching, mode toggling, or other context-changing actions.
 *
 * @returns The redirect URL if a redirect is needed, or null if the user
 * should stay on the current page
 *
 * @example
 * // User at /vaults/abc123 switching accounts
 * const redirectUrl = getRedirectUrl()
 * if (redirectUrl) {
 *   await navigate({ to: redirectUrl }) // Redirects to /vaults
 * }
 * window.location.reload()
 */
export function getRedirectUrl(): string | null {
  const currentPath = window.location.pathname

  if (currentPath.startsWith('/vaults/') && currentPath !== '/vaults') {
    return '/vaults'
  }

  // Both the envelope private view (`/envelopes/:id`) and the envelope
  // editor (`/envelope/:id`) load a record scoped to the active sandbox
  // mode; after a switch the API no longer returns it, so fall back to the
  // list. The create page (`/envelope`, no id) carries no record and stays.
  if (isEnvelopesListPath(currentPath)) {
    return null
  }
  if (currentPath.startsWith('/envelopes/') && currentPath !== '/envelopes') {
    return '/envelopes'
  }
  if (currentPath.startsWith('/envelope/') && currentPath !== '/envelope') {
    return '/envelopes'
  }

  // Same for the template detail (`/templates/:id`) and editor
  // (`/template/:id`). The create page (`/template`, no id) stays.
  if (currentPath.startsWith('/templates/') && currentPath !== '/templates') {
    return '/templates'
  }
  if (currentPath.startsWith('/template/') && currentPath !== '/template') {
    return '/templates'
  }

  return null
}

/**
 * Redirects away from a record-scoped page before a context switch
 * (sandbox toggle, account switch) and reports whether the caller may
 * proceed with the switch.
 *
 * The switch must NOT mutate the active scope (sandbox header / account)
 * or clear the query cache until this resolves `true`. Mutating first
 * leaves the still-mounted edit page refetching the old record id under
 * the new scope — a guaranteed 404 — and if an unsaved-changes blocker
 * suspends the navigation, the page spins on that 404 indefinitely.
 *
 * @returns `true` when there was nothing to leave or the navigation
 * reached the target; `false` when an unsaved-changes prompt was
 * cancelled (the user stayed), in which case the switch must be aborted.
 */
export async function redirectBeforeContextSwitch(
  navigate: (to: string) => Promise<unknown>,
): Promise<boolean> {
  const redirectUrl = getRedirectUrl()
  if (!redirectUrl) return true

  try {
    await navigate(redirectUrl)
  } catch {
    // Blocked navigation that the user cancelled rejects here.
    return false
  }

  // A blocker that the user reset can resolve without moving — confirm we
  // actually landed on the target before allowing the switch to continue.
  return window.location.pathname === redirectUrl
}

/**
 * Returns the default page size based on screen dimensions.
 * Uses the same breakpoint (48rem) as Tailwind's `md:` and `useIsMobile`.
 *
 * - Mobile (viewport below md breakpoint) → 10 (natural scroll)
 * - Tall screens (viewport height >= 1000px) → 10 (enough space)
 * - Otherwise → 5
 */
export function getDefaultPageSize(): number {
  if (typeof window === 'undefined') return 5
  const isMobile = !window.matchMedia('(min-width: 48rem)').matches
  const isTallScreen = window.innerHeight >= 1000
  return isMobile || isTallScreen ? 10 : 5
}
